import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AxiosController from "../authentication/AxiosController";
import dateFormat from "dateformat";

const DeactivateInactiveUsers = ({ show, setShow, slug, lang }) => {
  const { t } = useTranslation("global");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(true);
  const [roles, setRoles] = useState(["affiliate"]);
  const [period, setPeriod] = useState(600);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const availableRoles = ["employee", "fulfillment", "seller", "affiliate"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    const requestBody = {
      roles: roles,
      period: parseInt(period),
      preview: preview
    };

    try {
      const token = localStorage.getItem("token");
      const response = await AxiosController.post(
        `api/${slug}/${lang}/admin/users/deactivate-inactive`,
        requestBody,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResponse(response.data);
    } catch (err) {
      console.error("Error deactivating inactive users:", err);
      setError(err.response?.data?.message || "An error occurred while processing the request");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setResponse(null);
    setError(null);
    setRoles(["affiliate"]);
    setPeriod(600);
    setPreview(true);
  };

  const handleRoleChange = (role) => {
    if (roles.includes(role)) {
      setRoles(roles.filter(r => r !== role));
    } else {
      setRoles([...roles, role]);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("panel.deactivateInactiveUsers")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t("panel.selectRoles")}</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {availableRoles.map((role) => (
                <Form.Check
                  key={role}
                  type="checkbox"
                  id={`role-${role}`}
                  label={t(`panel.${role}`)}
                  checked={roles.includes(role)}
                  onChange={() => handleRoleChange(role)}
                  className="me-3"
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              {t("panel.rolesDescription")}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("panel.inactivePeriod")} (days)</Form.Label>
            <Form.Control
              type="number"
              min="14"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="600"
            />
            <Form.Text className="text-muted">
              {t("panel.periodDescription")}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="preview-mode"
              label={t("panel.previewMode")}
              checked={preview}
              onChange={(e) => setPreview(e.target.checked)}
            />
            <Form.Text className="text-muted">
              {t("panel.previewDescription")}
            </Form.Text>
          </Form.Group>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {response && (
            <>
              <Alert variant="info" className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{t("panel.response")}:</h6>
                  {response?.data?.count != null && (
                    <span className="badge bg-secondary">{response.data.count}</span>
                  )}
                </div>
                {response?.message && (
                  <div className="mt-2">{response.message}</div>
                )}
              </Alert>
              {Array.isArray(response?.data?.accounts) && response.data.accounts.length > 0 ? (
                <div className="table-responsive"style={{maxHeight: "300px", overflowY: "auto"}} >
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                         <th>{t("panel.createdAt") || "Created at"}</th>
                         <th>{t("panel.user")  }</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response.data.accounts.map((account) => (
                        <tr key={account.id}>
                           <td>{dateFormat(account["created_at"], "dd/mm/yyyy ")} <br />
                           {dateFormat(account["created_at"], " h:MM TT")}</td>
                           <td>  {account.id}# {t(`panel.${account.role}`)} <br /> {account.first_name} {account.last_name} <br /> {account.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="light" className="mt-2">
                  {t("panel.noAccountsFound") || "No accounts found"}
                </Alert>
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("panel.close")}
        </Button>
        <Button 
          variant="danger" 
          onClick={handleSubmit} 
          disabled={loading || roles.length === 0}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {t("panel.processing")}
            </>
          ) : (
            preview ? t("panel.previewDeactivation") : t("panel.deactivateUsers")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeactivateInactiveUsers;

